import {LogLevel, NfcCard} from "@variocube/app-api";
import {soundMachine} from "@variocube/app-host/src/SoundMachine";
import {
	BackButton,
	Badge,
	classNames,
	layout,
	PushButton,
	TextInput,
	typography,
	useAsyncCallback,
	useCardListener,
	usePlatform,
	View,
	ViewContent,
	ViewDescription,
	ViewHeader,
	ViewHeaderItem,
	ViewTitle,
} from "@variocube/cube-ui";
import * as React from "react";
import {Fragment, useCallback, useEffect, useState} from "react";
import {getSoundByKey} from "../accessibility";
import {useLocalization} from "../i18n";
import {commonStyles} from "./commonStyles";
import {logger} from "./logger";
import {UserAccessToken, UserAccessTokens} from "./model";

interface RegisterRfidViewProps {
	onBack: () => void;
	userAccessToken: UserAccessToken;
	userAccessTokens: UserAccessTokens | undefined;
	putTokenUrl: string;
}

enum Flow {
	WaitingForChip,
	WaitingForRegistration,
	Registered,
}

const log = logger.sub("RegisterRfidView");

export function RegisterRfidView({onBack, userAccessToken, putTokenUrl, userAccessTokens}: RegisterRfidViewProps) {
	const {t, language} = useLocalization();
	const platform = usePlatform();
	const [token, setToken] = useState<string>("");
	const [flow, setFlow] = useState<Flow>(Flow.WaitingForChip);
	const [tokenAlreadyExistsError, setTokenAlreadyExistsError] = React.useState(false);

	const title = [
		t("RegisterRfidInstruction1"),
		t("RegisterRfidInstruction2"),
		t("RegisterRfidInstruction3"),
	][flow];

	const register = useAsyncCallback(async () => {
		if (!token) return;
		const payload = {
			...userAccessToken,
			token,
		};
		log.info("Check if token already exists", userAccessTokens, token);
		if (userAccessTokens && Object.keys(userAccessTokens).some(x => userAccessTokens[x].token == token)) {
			setTokenAlreadyExistsError(true);
			return;
		}
		log.info("Registering token", payload);
		const response = await fetch(putTokenUrl, {
			method: "POST",
			body: JSON.stringify(payload),
			headers: {
				"Content-Type": "application/json",
				"Accept": "application/json",
			},
		});
		if (!response.ok) {
			log.error("Response status not okay", response.status);
			soundMachine.play(getSoundByKey("RegistrationFailed", language));
			throw new Error("Response status not okay: " + response.status);
		}
		platform.log(LogLevel.Audit, "Token registered", payload);
		setFlow(Flow.Registered);
	}, [putTokenUrl, userAccessToken, token, t, language]);

	const handleCard = useCallback((card?: NfcCard) => {
		if (card) {
			setToken(card.uid);
			setFlow(Flow.WaitingForRegistration);
			register.execute();
		}
	}, [register]);
	useCardListener(handleCard);

	useEffect(() => {
		switch (flow) {
			case Flow.WaitingForChip:
				soundMachine.play(getSoundByKey("SpeakWaitingForChip", language));
				break;
			case Flow.WaitingForRegistration:
				soundMachine.play(getSoundByKey("SpeakRegisteringChip", language));
				break;
			case Flow.Registered:
				soundMachine.play(getSoundByKey("SpeakChipRegistered", language));
				break;
		}
	}, [flow, t, language]);

	const titleToShow = React.useMemo(() => {
		return tokenAlreadyExistsError ? t("RegisterRfidInstruction4") : title;
	}, [tokenAlreadyExistsError, title]);

	return (
		<View>
			<ViewHeader className={commonStyles.viewHeader}>
				<ViewHeaderItem>
					<BackButton onClick={onBack} label={t("Back")} />
				</ViewHeaderItem>
				<ViewHeaderItem fill>
					<ViewTitle>{t("RegisterRfidTitle")}</ViewTitle>
					<ViewDescription>{titleToShow}</ViewDescription>
				</ViewHeaderItem>
			</ViewHeader>
			<ViewContent className={classNames(layout.vertical, layout.center, layout.alignCenter)}>
				<div className={classNames(layout.marginLarge, typography.title)}>
					{titleToShow}
				</div>
				<div className={classNames(layout.marginLarge)}>
					<TextInput
						value={token}
						placeholder={t("WaitingForChip")}
						backspace={false}
						outline
					/>
				</div>
				<div className={classNames(layout.horizontal, layout.spaceBetween)} style={{width: "66%"}}>
					{flow == Flow.Registered || tokenAlreadyExistsError
						? (
							<PushButton primary onClick={onBack}>
								{t("Finish")}
							</PushButton>
						)
						: (
							<Fragment>
								<PushButton onClick={onBack}>
									{t("Cancel")}
								</PushButton>
								<PushButton
									primary
									disabled={register.loading || token.length == 0}
									onClick={register.execute}
								>
									{t("Register")}
								</PushButton>
							</Fragment>
						)}
				</div>
				<div
					className={layout.marginLarge}
					style={{visibility: tokenAlreadyExistsError ? "visible" : "hidden"}}
				>
					<Badge error>
						{t("RFIDAlreadyRegistered")}
					</Badge>
				</div>
				<div className={layout.marginLarge} style={{visibility: register.error ? "visible" : "hidden"}}>
					<Badge error>
						{t("RegistrationFailed")}
					</Badge>
				</div>
			</ViewContent>
		</View>
	);
}
